import React from 'react';
import PropTypes from 'prop-types';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import './PlatformHero.css';

// ---------------------------------------------------------------------------
// Decorative constellation backdrop. The platform IS a network of connected
// ecosystems, so a node-and-edge motif is the literal product metaphor rather
// than arbitrary ornament. Pure inline SVG (no JS, no force-graph dependency)
// keeps it cheap and server-renderable; it sits behind the hero copy and is
// fully decorative, so it carries aria-hidden and is invisible to assistive
// tech. Coordinates are on a 0–100 viewBox and the SVG scales with the hero.
// ---------------------------------------------------------------------------
const CONSTELLATION_NODES = [
    { cx: 12, cy: 22, r: 1.6 },
    { cx: 28, cy: 14, r: 1.1 },
    { cx: 22, cy: 40, r: 2.2 },
    { cx: 44, cy: 30, r: 1.3 },
    { cx: 38, cy: 58, r: 1.1 },
    { cx: 60, cy: 18, r: 1.5 },
    { cx: 70, cy: 42, r: 2.4 },
    { cx: 56, cy: 60, r: 1.2 },
    { cx: 82, cy: 26, r: 1.3 },
    { cx: 88, cy: 56, r: 1.7 },
    { cx: 76, cy: 74, r: 1.1 },
    { cx: 18, cy: 70, r: 1.4 },
    { cx: 40, cy: 82, r: 1.6 },
    { cx: 62, cy: 84, r: 1.2 },
];

// Edges reference node indices above. Kept sparse so the field reads as a
// connected graph rather than visual noise.
const CONSTELLATION_EDGES = [
    [0, 2],
    [1, 3],
    [2, 3],
    [3, 5],
    [3, 6],
    [4, 7],
    [5, 8],
    [6, 7],
    [6, 9],
    [8, 9],
    [9, 10],
    [2, 11],
    [4, 12],
    [7, 13],
    [11, 12],
    [12, 13],
];

function ConstellationBackdrop() {
    return (
        <svg
            className="platform-hero-constellation"
            viewBox="0 0 100 100"
            preserveAspectRatio="xMidYMid slice"
            aria-hidden="true"
            focusable="false"
        >
            <g className="platform-hero-constellation-edges">
                {CONSTELLATION_EDGES.map(([from, to]) => {
                    const a = CONSTELLATION_NODES[from];
                    const b = CONSTELLATION_NODES[to];
                    return (
                        <line
                            key={`${from}-${to}`}
                            x1={a.cx}
                            y1={a.cy}
                            x2={b.cx}
                            y2={b.cy}
                        />
                    );
                })}
            </g>
            <g className="platform-hero-constellation-nodes">
                {CONSTELLATION_NODES.map((node) => (
                    <circle
                        key={`${node.cx}-${node.cy}`}
                        cx={node.cx}
                        cy={node.cy}
                        r={node.r}
                    />
                ))}
            </g>
        </svg>
    );
}

// ---------------------------------------------------------------------------
// Shared hero: an eyebrow label, a headline, a lead paragraph, and up to two
// CTAs. The headline defaults to `pageTitle` (keeps the About / Mission pages
// using their route title as the visible h1), but the Landing page overrides
// it with a bold value-proposition headline while still carrying the brand
// name in the eyebrow.
//
// Every platform hero shares one composed treatment: the brand-palette
// gradient, the constellation backdrop, a white headline, and a consistent
// amber eyebrow. There is no flat-navy variant: a uniform header identity
// across the apex (/, /about, /mission) reads as designed rather than as an
// empty solid void. The eyebrow and headline colors are owned entirely by the
// CSS module (no Bootstrap text-color / weight utilities on the markup) so no
// !important override is needed to win the cascade.
// ---------------------------------------------------------------------------
export default function PlatformHero({
    pageTitle,
    headline,
    eyebrow,
    lead,
    primaryCta,
    secondaryCta,
}) {
    return (
        <section className="platform-hero d-flex align-items-center text-white position-relative overflow-hidden">
            <ConstellationBackdrop />
            <Container className="position-relative platform-hero-content">
                <Row className="justify-content-center">
                    <Col xs={12} md={10} lg={9} xl={8} className="text-center">
                        <p className="text-uppercase mb-3 platform-hero-eyebrow">
                            {eyebrow}
                        </p>
                        <h1 className="platform-hero-headline fw-bold mb-4">
                            {headline || pageTitle}
                        </h1>
                        <p className="lead mb-5 opacity-90 platform-hero-lead">
                            {lead}
                        </p>
                        <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                            <Button
                                as={Link}
                                to={primaryCta.to}
                                size="lg"
                                variant="accent"
                                className="fw-semibold px-4 rounded-pill"
                            >
                                {primaryCta.label}
                            </Button>
                            {secondaryCta ? (
                                <Button
                                    as={Link}
                                    to={secondaryCta.to}
                                    size="lg"
                                    variant="outline-light"
                                    className="fw-semibold px-4 rounded-pill"
                                >
                                    {secondaryCta.label}
                                </Button>
                            ) : null}
                        </div>
                    </Col>
                </Row>
            </Container>
        </section>
    );
}

PlatformHero.propTypes = {
    pageTitle: PropTypes.string.isRequired,
    headline: PropTypes.string,
    eyebrow: PropTypes.string.isRequired,
    lead: PropTypes.string.isRequired,
    primaryCta: PropTypes.shape({
        to: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
    }).isRequired,
    secondaryCta: PropTypes.shape({
        to: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
    }),
};

PlatformHero.defaultProps = {
    headline: null,
    secondaryCta: null,
};
